import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  markSessionExpired,
  resetSessionExpiry,
  sessionExpiryReason,
  subscribeSessionExpired,
  SESSION_EXPIRY_REASONS,
} from './sessionExpiry';

/**
 * Cuánto se espera antes de avisar que el token venció.
 *
 * <p>`addAccessTokenExpired` dispara aunque haya una renovación en vuelo, y los timers de
 * oidc-client-ts comparan contra reloj de pared con un intervalo real de 5 s. Al despertar una
 * laptop suspendida, `expiring` y `expired` llegan casi juntos: la renovación arranca al mismo
 * tiempo que el aviso. Sin esta espera el usuario ve un modal que se cierra solo — y en el medio
 * puede apretar Enter sobre un botón que es irreversible.
 */
export const GRACIA_MS = 4000;

/**
 * Backoff de los reintentos de renovación.
 *
 * <p>`SilentRenewService` (oidc-client-ts) sólo reprograma ante `ErrorTimeout`; ante cualquier
 * otro error llama `_raiseSilentRenewError` y no reintenta nunca más, porque el siguiente
 * `expiring` sólo se arma tras un `USER_LOADED` que ya no va a llegar. Un `fetch` fallido tira
 * `TypeError`, no `ErrorTimeout`, así que un microcorte de red de dos segundos condena la sesión
 * a morir una hora después, sin rastro. Esto es lo que le faltaba.
 */
export const REINTENTOS_MS = [5000, 15000, 45000];

/**
 * Observa todo lo que puede significar "la sesión dejó de servir" y devuelve el motivo a mostrar,
 * o `null`.
 *
 * <p>Lo consume `AutoLogin`, que es el <b>único dueño</b> de decidir qué ve el usuario cuando la
 * sesión está rota. Tener dos componentes decidiendo eso es el modo de falla que el backlog ya
 * tiene abierto como #131.
 */
export const useSessionExpiry = () => {
  const auth = useAuth();
  const [motivo, setMotivo] = useState(() => sessionExpiryReason());
  const graciaRef = useRef(null);
  const reintentoRef = useRef(null);
  const reintentandoRef = useRef(false);
  // Generación de la cadena de reintentos. `cancelarTimers` no tiene forma de avisarle a una
  // cadena que ya está en vuelo —el `signinSilent` pendiente va a volver igual—, así que cada
  // cadena se queda con la época en la que nació y se retira sola si quedó obsoleta. Sin esto,
  // una cadena cancelada podía rearmar su timer y hasta solaparse con otra, pisando el mismo
  // ref (#172).
  const epocaRef = useRef(0);

  const cancelarTimers = () => {
    epocaRef.current += 1;
    clearTimeout(graciaRef.current);
    clearTimeout(reintentoRef.current);
    graciaRef.current = null;
    reintentoRef.current = null;
    reintentandoRef.current = false;
  };

  // El estado compartido manda y acá sólo se refleja. La gracia NO vive en esta traducción:
  // vive antes de marcar (ver `alVencer`), porque si no el singleton diría "sesión muerta"
  // durante la ventana de gracia y los pollers —que leen el singleton, no este estado— frenarían
  // aunque la renovación esté por entrar bien. Pasó de largo en la primera versión.
  useEffect(() => {
    const desuscribir = subscribeSessionExpired((razon) => setMotivo(razon));
    return () => {
      desuscribir();
      // El otro efecto puede no haberse registrado nunca (mocks sin `auth.events`), así que la
      // limpieza del timer no puede depender de él.
      clearTimeout(graciaRef.current);
    };
  }, []);

  // Reintentos propios de renovación. Sólo uno en curso a la vez. Va en un ref y no en un
  // useCallback porque lo llaman listeners registrados una sola vez: si cambiara de identidad
  // habría que re-suscribirlos en cada render.
  const arrancarReintentos = useRef(null);
  arrancarReintentos.current = () => {
    if (reintentandoRef.current) return;
    reintentandoRef.current = true;

    const epoca = epocaRef.current;
    const obsoleta = () => epoca !== epocaRef.current;

    const intentar = (i) => {
      if (obsoleta()) return;
      if (i >= REINTENTOS_MS.length) {
        reintentandoRef.current = false;
        markSessionExpired(SESSION_EXPIRY_REASONS.RENEW_FAILED);
        return;
      }
      reintentoRef.current = setTimeout(() => {
        if (obsoleta()) return;
        Promise.resolve()
          .then(() => auth?.signinSilent?.())
          .then((usuario) => {
            if (obsoleta()) return;
            // OJO: `signinSilent` NO rechaza. react-oidc-context atrapa el error, despacha ERROR y
            // devuelve `null`, así que un `.catch` solo dejaría la escalera en un único intento y
            // haría inalcanzable a RENEW_FAILED. El fallo se reconoce por el retorno vacío.
            if (!usuario) {
              intentar(i + 1);
              return;
            }
            // Entró: `addUserLoaded` ya se encargó de limpiar el estado.
            reintentandoRef.current = false;
          })
          .catch(() => { if (!obsoleta()) intentar(i + 1); });
      }, REINTENTOS_MS[i]);
    };

    intentar(0);
  };

  // Eventos del UserManager. El guard no es defensivo de más: 32 suites mockean
  // react-oidc-context inline y ninguna expone `events`.
  const events = auth?.events;
  useEffect(() => {
    if (!events?.addAccessTokenExpired) return undefined;

    // El evento dispara aunque haya una renovación en vuelo: los timers de oidc-client-ts
    // comparan contra reloj de pared con un intervalo real de 5 s, así que al despertar una
    // laptop suspendida `expiring` y `expired` llegan casi juntos. Se espera la gracia ANTES de
    // marcar, para que nadie —ni el aviso ni los pollers— actúe sobre una sesión que está por
    // recuperarse sola.
    const alVencer = () => {
      clearTimeout(graciaRef.current);
      graciaRef.current = setTimeout(
        () => markSessionExpired(SESSION_EXPIRY_REASONS.TOKEN_EXPIRED),
        GRACIA_MS,
      );
    };
    const alFallarRenovacion = () => arrancarReintentos.current();
    const alCargarUsuario = () => {
      cancelarTimers();
      resetSessionExpiry();
    };

    events.addAccessTokenExpired(alVencer);
    events.addSilentRenewError(alFallarRenovacion);
    events.addUserLoaded(alCargarUsuario);

    return () => {
      events.removeAccessTokenExpired?.(alVencer);
      events.removeSilentRenewError?.(alFallarRenovacion);
      events.removeUserLoaded?.(alCargarUsuario);
      cancelarTimers();
    };
  }, [events]);

  // El mismo hecho que `addSilentRenewError`, pero ya normalizado por react-oidc-context.
  // `source` es un discriminante tipado de la librería: mucho más firme que matchear el
  // texto del mensaje. Es redundante a propósito — markSessionExpired es idempotente— y
  // cubre que un provider futuro cambie de camino.
  //
  // El filtro es exclusivo: los otros `source` son errores de LOGIN, y de ésos se ocupa la
  // pantalla completa de AutoLogin (#136). Si el aviso los tomara, la taparía.
  const errorSource = auth?.error?.source;
  useEffect(() => {
    if (errorSource === 'renewSilent') arrancarReintentos.current();
  }, [errorSource]);

  return motivo;
};
